/**
 * GearCatalogTable Component
 *
 * Table view for gear catalog with sortable columns
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-301
 */

import { Edit, Trash, History, Package, ChevronUp, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import Badge from '../common/Badge';

/**
 * Map item type to Badge color
 */
function getTypeBadgeColor(itemType) {
  const colors = {
    gear: 'blue',
    other: 'gray'
  };
  return colors[itemType] || 'gray';
}

/**
 * Sort icon component
 */
function SortIcon({ field, sortField, sortOrder }) {
  if (field !== sortField) {
    return (
      <div className="inline-flex flex-col ml-1 opacity-30">
        <ChevronUp className="w-3 h-3 -mb-1" />
        <ChevronDown className="w-3 h-3" />
      </div>
    );
  }

  return sortOrder === 'asc' ? (
    <ChevronUp className="w-4 h-4 ml-1 inline" />
  ) : (
    <ChevronDown className="w-4 h-4 ml-1 inline" />
  );
}

export default function GearCatalogTable({
  gear,
  sortField,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
  onToggleActive,
  onViewAudit
}) {
  const [hoveredRow, setHoveredRow] = useState(null);

  /**
   * Sortable column header
   */
  const SortableHeader = ({ field, label }) => (
    <th
      onClick={() => onSort(field)}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
    >
      <div className="flex items-center">
        {label}
        <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Image
            </th>
            <SortableHeader field="gear_name" label="Gear Name" />
            <SortableHeader field="gear_category" label="Category" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Subcategory
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              L1 / L2 / L3 Associations
            </th>
            <SortableHeader field="item_type" label="Type" />
            <SortableHeader field="typical_cost_usd" label="Typical Cost" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {gear.map((g) => (
            <tr
              key={g.id}
              onMouseEnter={() => setHoveredRow(g.id)}
              onMouseLeave={() => setHoveredRow(null)}
              className={`transition-colors ${
                hoveredRow === g.id ? 'bg-gray-50' : ''
              }`}
            >
              {/* Image */}
              <td className="px-6 py-4 whitespace-nowrap">
                {g.photo_url ? (
                  <img
                    src={g.photo_url}
                    alt={g.gear_name}
                    className="w-16 h-16 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                    <Package className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </td>

              {/* Gear Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {g.gear_name}
                </div>
                {g.brand_names && g.brand_names.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Brands: {g.brand_names.slice(0, 2).join(', ')}
                    {g.brand_names.length > 2 && '...'}
                  </div>
                )}
              </td>

              {/* Gear Category */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{g.gear_category || '—'}</div>
              </td>

              {/* Gear Subcategory */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-600">
                  {g.gear_subcategory || '—'}
                </div>
              </td>

              {/* Category Associations (L1 / L2 / L3) */}
              <td className="px-6 py-4 min-w-[300px] max-w-[400px]">
                {g.category_associations && g.category_associations.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {g.category_associations.map((assoc, idx) => (
                      <div key={assoc.id || idx} className="text-xs">
                        <span className="font-medium text-blue-700">
                          {assoc.category_l1_name || `L1:${assoc.category_l1_id}`}
                        </span>
                        {assoc.category_l2_name && (
                          <>
                            <span className="text-gray-400 mx-1">›</span>
                            <span className="text-blue-600">{assoc.category_l2_name}</span>
                          </>
                        )}
                        {assoc.category_l3_name && (
                          <>
                            <span className="text-gray-400 mx-1">›</span>
                            <span className="text-blue-500">{assoc.category_l3_name}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">—</div>
                )}
              </td>

              {/* Item Type */}
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge color={getTypeBadgeColor(g.item_type)}>
                  {g.item_type}
                </Badge>
              </td>

              {/* Typical Cost */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {g.typical_cost_usd
                    ? `$${parseFloat(g.typical_cost_usd).toFixed(2)}`
                    : '—'}
                </div>
                {g.commonly_available_for_rental && (
                  <div className="text-xs text-green-600 mt-1">
                    Rental: ${g.rental_cost_per_day_usd ? parseFloat(g.rental_cost_per_day_usd).toFixed(2) : '?'}/day
                  </div>
                )}
              </td>

              {/* Status Toggle */}
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => onToggleActive(g.id, g.is_active)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    g.is_active ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={g.is_active}
                  title={g.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                >
                  <span className="sr-only">
                    {g.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      g.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </td>

              {/* Actions */}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-3">
                  {/* Edit Button */}
                  <button
                    onClick={() => onEdit(g)}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                    title="Edit gear"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </button>

                  {/* Audit Button */}
                  <button
                    onClick={() => onViewAudit(g)}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded transition-colors"
                    title="View audit history"
                  >
                    <History className="w-4 h-4 mr-1" />
                    Audit
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => onDelete(g)}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                    title="Deactivate gear"
                  >
                    <Trash className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
